import { decode } from 'js-base64';
import { NotificationCard } from '../../components/Card';
import { Avatar, Button, Center, Flex, Icon, Text } from '@chakra-ui/react';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationLink } from '../../components/NotificationLink';
import { NotificationTime } from '../../components/Time';
import { RiEditLine } from 'react-icons/ri';
import {
  Hackerrank,
  Hackerearth,
  CriteriaCorp,
  Hireflix,
} from '@/assets/icons/ats';

const FillAssessmentScorecard = ({ notification = {}, onClose = () => {} }) => {
  const [markAsRead] = useMarkAsReadMutation();
  const { custom_attributes } = notification;
  const data = JSON.parse(decode(custom_attributes));

  const {
    candidate,
    job,
    assessment,
    application_id,
    is_reminder = false,
    requested_by = {},
  } = data;
  const {
    id: candidateId,
    avatar_url: candidateAvatarUrl,
    name: candidateName,
  } = candidate;
  const { name: jobName } = job;
  const {
    id: assessmentId,
    name: assessmentName,
    platform: assessmentPlatform,
  } = assessment || {};

  const getPlatformConfig = (platform) => {
    switch (platform) {
      case 'hackerrank':
        return {
          bg: 'black',
          icon: Hackerrank,
        };
      case 'hackerearth':
        return {
          bg: 'black',
          icon: Hackerearth,
        };
      case 'criteriacorp':
        return {
          bg: '#09264E',
          icon: CriteriaCorp,
          iconProps: {
            width: '18px',
            height: '18px',
          },
        };
      case 'hireflix':
        return {
          bg: '#5862FF',
          icon: Hireflix,
          iconProps: {
            width: '18px',
            height: '18px',
          },
        };
      default:
        return {
          bg: 'ats-tertiary',
          icon: RiEditLine,
        };
    }
  };

  const platformConfig = getPlatformConfig(assessmentPlatform);

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="46px" h="46px" borderRadius="full" bg={platformConfig.bg}>
        <Icon
          as={platformConfig.icon}
          color="ats-primary"
          {...platformConfig.iconProps}
        />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
        <Text>
          <Text as="span" fontSize="sm">
            {is_reminder ? `Don't forget to evaluate` : `Evaluate`}
          </Text>
          <Text as="span" size="sm-semi-bold">
            <NotificationLink
              to={`/contacts/${candidateId}`}
              label={`${candidateName}'s`}
              onClose={onClose}
              notification={notification}
            />
          </Text>
          <Text as="span" fontSize="sm">
            {' '}
            assessment
          </Text>
        </Text>
        <Text size="sm-semi-bold">Details</Text>
        <Flex
          border="1px solid"
          borderColor="border-color"
          borderRadius="sm"
          p="14px"
          flexDir="column"
          gap="6px"
          mt="-6px"
        >
          <Text size="sm-semi-bold">Candidate</Text>
          <Flex mb="6px" gap="6px">
            <Avatar
              size="2xs"
              borderRadius="4px"
              className="avatar-xs"
              src={candidateAvatarUrl}
              name={candidateName}
            />
            <Text fontSize="sm">{candidateName}</Text>
          </Flex>
          <Text size="sm-semi-bold">Job</Text>
          <Text fontSize="sm" mb="6px">
            {jobName}
          </Text>
          <Text size="sm-semi-bold">Assessment</Text>
          <Text fontSize="sm" mb="6px">
            {assessmentName}
          </Text>
          {requested_by?.name && (
            <>
              <Text size="sm-semi-bold">Requested by</Text>
              <Flex mb="6px" gap="6px">
                <Avatar
                  size="2xs"
                  borderRadius="4px"
                  className="avatar-xs"
                  src={requested_by?.avatar_url}
                  name={requested_by?.name}
                />
                <Text fontSize="sm">{requested_by?.name}</Text>
              </Flex>
            </>
          )}
        </Flex>
        <NotificationTime notification={notification} />
        <Button
          w="fit-content"
          variant="ats-primary"
          borderRadius="8px"
          onClick={() => {
            markAsRead({ id: notification?.id });
            window.open(
              `/dashboard?assessment_id=${assessmentId}&app_id=${application_id}`,
              '_self'
            );
            onClose();
          }}
          maxH="34px"
          data-test-id="evaluate-assessment-button"
        >
          Evaluate assessment
        </Button>
      </Flex>
    </Flex>
  );
};

export const FillAssessmentScorecardNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <FillAssessmentScorecard notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
